<template>
    <nav class="entity-path" aria-label="breadcrumb">
      <div v-if="loading" class="entity-path__loading">Loading…</div>
      <div v-else-if="error" class="entity-path__error">Couldn’t load path</div>
      <div v-else-if="segments && segments.length" class="entity-path__inner">
        <!-- Render parents from API -->
        <span v-for="(seg, idx) in segments" :key="seg.type + '-' + seg.id">
          <span class="entity-path__label">{{ labels[seg.type] }}:</span>
          <router-link :to="seg.route">{{ seg.name }}</router-link>
          <span v-if="idx < segments.length - 1" class="entity-path__sep"> / </span>
        </span>
  
        <!-- Optional leaf (current entity) -->
        <template v-if="includeLeaf && leafName">
          <span class="entity-path__sep"> / </span>
          <span class="entity-path__leaf">{{ leafName }}</span>
        </template>
      </div>
    </nav>
  </template>
  
  <script>
  import axios from 'axios'
  import { mapState } from 'vuex'
  import { API_ENDPOINTS } from '@/services/config'
  
  /**
   * Props:
   *  - type: 'project' | 'workflow' | 'action' | 'experiment'
   *  - id: string|number (entity id)
   *  - leafName?: string  -> Optional trailing label for the current entity (e.g., experiment.name)
   *  - includeLeaf?: bool -> Whether to render the leafName (default: true)
   *  - overrideLabels?: { project?: string, workflow?: string, action?: string }
   */
  export default {
    name: 'EntityPathBar',
    props: {
      type: { type: String, required: true },
      id: { type: [String, Number], required: true },
      leafName: { type: String, default: '' },
      includeLeaf: { type: Boolean, default: true },
      overrideLabels: { type: Object, default: () => ({}) }
    },
    data() {
      return {
        loading: false,
        error: null,
        segments: [] // [{ type, id, name, route }]
      }
    },
    computed: {
      ...mapState(['loggedInUser']),
      labels() {
        // Defaults; you used “Activity” in your markup
        const base = { project: 'Project', workflow: 'Workflow', action: 'Activity' }
        return { ...base, ...this.overrideLabels }
      }
    },
    watch: {
      type: 'fetchPath',
      id: 'fetchPath'
    },
    created() {
      this.fetchPath()
    },
    methods: {
      async fetchPath() {
        this.loading = true
        this.error = null
        try {
          const params = { entra_id: this.loggedInUser?.entra_id }
          if (this.type === 'experiment') params.experiment_id = this.id
          else if (this.type === 'action') params.action_id = this.id
          else if (this.type === 'workflow') params.workflow_id = this.id
          else if (this.type === 'project') {
            // No API call needed for a bare project page; synthesize segment
            this.segments = [{ type: 'project', id: this.id, name: this.leafName || '', route: `/projects/${this.id}` }]
            this.loading = false
            return
          }
  
          const res = await axios.get(API_ENDPOINTS.path, { params })
          const data = res.data || {}
  
          // Normalize from API shape { project, workflow, action } -> array with routes
          const order = ['project', 'workflow', 'action']
          const segs = order
            .map(t => (data[t] ? { type: t, ...data[t] } : null))
            .filter(Boolean)
            .map(seg => ({
              ...seg,
              route:
                seg.type === 'project' ? `/projects/${seg.id}` :
                seg.type === 'workflow' ? `/workflows/${seg.id}` :
                seg.type === 'action'   ? `/actions/${seg.id}` : '#'
            }))
  
          this.segments = segs
        } catch (e) {
          console.error('EntityPathBar error:', e)
          this.error = e
        } finally {
          this.loading = false
        }
      }
    }
  }
  </script>
  
  <style scoped>
  .entity-path {
    font-size: .95rem;
    margin: 4px 0 12px;
    padding: 8px 12px;
    border-radius: 8px;
    background: #f6f7fb;
    display: inline-block;
  }
  .entity-path__label {
    opacity: .8;
    margin-right: 4px;
  }
  .entity-path__sep {
    margin: 0 6px;
    opacity: .6;
  }
  .entity-path__leaf {
    font-weight: 600;
  }
  .entity-path__loading,
  .entity-path__error {
    opacity: .85;
  }
  </style>
  